/**
 * A custom hook that retrieves conversations data categorized by their completion status.
 *
 * This hook makes separate API calls:
 * - One for completed conversations (COMPLETED filter) with pagination
 * - One for incomplete conversations (INCOMPLETE filter) with pagination
 * - One for screened out conversations (SCREENED_OUT filter) with pagination
 *
 * On initial load (no filters), all views are fetched to get counts.
 * When filters are applied, only the active view is fetched to avoid unnecessary API calls.
 *
 * Pagination state is managed externally via URL parameters.
 */
import { useEffect, useRef } from 'react';
import { useGetAllConversations } from '@/reactQuery/get';
import { TranscriptsView } from '@/enums';
import { SortConfig } from '@/types';
import useConversationsStore from '@/store/ConversationsStore';
import {
  conversationsActionsSelector,
  completedConversationsSelector,
  incompleteConversationsSelector,
  screenedOutConversationsSelector,
} from '@/selectors/conversations';

const PAGE_SIZE = 20;

export const useConversationsByStatus = (
  filterId: string | null,
  language: string | null,
  refetchInterval: number,
  currentProjectIsTranslating: boolean,
  projectId?: string,
  urlCurrentPage: number = 1,
  activeView: TranscriptsView = TranscriptsView.COMPLETED,
  selectedTranscript: boolean = false,
  conversationFromQuestionsTab?: boolean,
  sortConfig?: SortConfig,
  searchFilters?: Record<string, string>,
) => {
  // Derive view flags from activeView
  const isCompletedView = activeView === TranscriptsView.COMPLETED;
  const isIncompleteView = activeView === TranscriptsView.INCOMPLETE;
  const isScreenedOutView = activeView === TranscriptsView.SCREENED_OUT;

  // Track initial load with selected transcript
  const isInitialSelectedTranscriptLoad = useRef(selectedTranscript);
  // Preserve counts across re-renders when not fetching all views
  const preservedCompletedCount = useRef<number>(0);
  const preservedIncompleteCount = useRef<number>(0);
  const preservedScreenedOutCount = useRef<number>(0);

  // Store access using selectors
  const {
    setCompletedConversations,
    setIncompleteConversations,
    setScreenedOutConversations,
    setCompletedTotalPages,
    setIncompleteTotalPages,
    setScreenedOutTotalPages,
  } = useConversationsStore(conversationsActionsSelector);
  const storeCompleted = useConversationsStore(completedConversationsSelector);
  const storeIncomplete = useConversationsStore(incompleteConversationsSelector);
  const storeScreenedOut = useConversationsStore(screenedOutConversationsSelector);

  // Store state checks
  const hasCompletedInStore = storeCompleted?.length > 0;
  const hasIncompleteInStore = storeIncomplete?.length > 0;
  const hasScreenedOutInStore = storeScreenedOut?.length > 0;

  // View and fetch conditions
  const shouldFetchTranslatedConversations =
    (!!language || currentProjectIsTranslating) && !hasCompletedInStore;

  // Base fetch conditions
  const shouldFetchCompleted = isCompletedView || shouldFetchTranslatedConversations;
  const shouldFetchIncomplete = !shouldFetchTranslatedConversations && isIncompleteView;
  const shouldFetchScreenedOut = !shouldFetchTranslatedConversations && isScreenedOutView;

  // Only skip fetching all views when we already have counts from a previous fetch
  const countsInStore =
    preservedCompletedCount.current > 0 &&
    preservedIncompleteCount.current > 0 &&
    preservedScreenedOutCount.current > 0;

  // Only fetch all views for counts
  const shouldFetchAllForCounts =
    (!shouldFetchTranslatedConversations && !filterId && !countsInStore) || !!filterId;

  // Navigation fetch conditions (for single transcript view when store is empty)
  const needsCompletedForNavigation = selectedTranscript && isCompletedView && !hasCompletedInStore;
  const needsIncompleteForNavigation =
    selectedTranscript && isIncompleteView && !hasIncompleteInStore;
  const needsScreenedOutForNavigation =
    selectedTranscript && isScreenedOutView && !hasScreenedOutInStore;

  const isViewingTranscriptDuringTranslation = selectedTranscript && currentProjectIsTranslating;

  // Final fetch decisions
  const shouldFetchCompletedData =
    (!selectedTranscript && (shouldFetchCompleted || shouldFetchAllForCounts)) ||
    (needsCompletedForNavigation &&
      !conversationFromQuestionsTab &&
      !isViewingTranscriptDuringTranslation);

  const shouldFetchIncompleteData =
    (!selectedTranscript && (shouldFetchIncomplete || shouldFetchAllForCounts)) ||
    needsIncompleteForNavigation;

  const shouldFetchScreenedOutData =
    (!selectedTranscript && (shouldFetchScreenedOut || shouldFetchAllForCounts)) ||
    needsScreenedOutForNavigation;

  // Fetch completed conversations
  const {
    data: { items: completedConversations, pagination: completedPagination } = {},
    isLoading: isLoadingCompleted,
  } = useGetAllConversations(
    projectId,
    filterId,
    shouldFetchCompletedData,
    language,
    refetchInterval,
    TranscriptsView.COMPLETED,
    { page: shouldFetchCompleted ? urlCurrentPage : 1, limit: PAGE_SIZE },
    sortConfig,
    searchFilters,
  );

  // Fetch incomplete conversations
  const {
    data: { items: incompleteConversations, pagination: incompletePagination } = {},
    isLoading: isLoadingIncomplete,
  } = useGetAllConversations(
    projectId,
    filterId,
    shouldFetchIncompleteData && !conversationFromQuestionsTab && !currentProjectIsTranslating,
    language,
    refetchInterval,
    TranscriptsView.INCOMPLETE,
    { page: shouldFetchIncomplete ? urlCurrentPage : 1, limit: PAGE_SIZE },
    sortConfig,
    searchFilters,
  );

  // Fetch screened out conversations
  const {
    data: { items: screenedOutConversations, pagination: screenedOutPagination } = {},
    isLoading: isLoadingScreenedOut,
  } = useGetAllConversations(
    projectId,
    filterId,
    shouldFetchScreenedOutData && !conversationFromQuestionsTab && !currentProjectIsTranslating,
    language,
    refetchInterval,
    TranscriptsView.SCREENED_OUT,
    { page: shouldFetchScreenedOut ? urlCurrentPage : 1, limit: PAGE_SIZE },
    sortConfig,
    searchFilters,
  );

  // Update store when conversations data changes
  useEffect(() => {
    // When page reloaded and we see the single transcript view, we should set store only once
    if (selectedTranscript && !isInitialSelectedTranscriptLoad.current) return;

    if (completedConversations?.length) {
      setCompletedConversations([...completedConversations]);
      isInitialSelectedTranscriptLoad.current = false;
    }
    if (incompleteConversations?.length) {
      setIncompleteConversations([...incompleteConversations]);
      isInitialSelectedTranscriptLoad.current = false;
    }
    if (screenedOutConversations?.length) {
      setScreenedOutConversations([...screenedOutConversations]);
      isInitialSelectedTranscriptLoad.current = false;
    }
    // Update total pages in store
    if (completedPagination?.totalPages) {
      setCompletedTotalPages(completedPagination.totalPages);
    }
    if (incompletePagination?.totalPages) {
      setIncompleteTotalPages(incompletePagination.totalPages);
    }
    if (screenedOutPagination?.totalPages) {
      setScreenedOutTotalPages(screenedOutPagination.totalPages);
    }
  }, [
    completedConversations,
    incompleteConversations,
    screenedOutConversations,
    setCompletedConversations,
    setIncompleteConversations,
    setScreenedOutConversations,
    selectedTranscript,
    setCompletedTotalPages,
    setIncompleteTotalPages,
    setScreenedOutTotalPages,
    completedPagination?.totalPages,
    incompletePagination?.totalPages,
    screenedOutPagination?.totalPages,
  ]);

  // Return loading state only for the currently active fetch
  const isLoading =
    (shouldFetchCompleted && isLoadingCompleted) ||
    (shouldFetchIncomplete && isLoadingIncomplete) ||
    (shouldFetchScreenedOut && isLoadingScreenedOut);

  // Update preserved counts when we have new pagination data
  useEffect(() => {
    if (completedPagination?.totalItems !== undefined) {
      preservedCompletedCount.current = completedPagination.totalItems;
    }
  }, [completedPagination?.totalItems]);

  useEffect(() => {
    if (incompletePagination?.totalItems !== undefined) {
      preservedIncompleteCount.current = incompletePagination.totalItems;
    }
  }, [incompletePagination?.totalItems]);

  useEffect(() => {
    if (screenedOutPagination?.totalItems !== undefined) {
      preservedScreenedOutCount.current = screenedOutPagination.totalItems;
    }
  }, [screenedOutPagination?.totalItems]);

  // Calculate counts - use current pagination if available, otherwise use preserved values
  const completedCount = completedPagination?.totalItems ?? preservedCompletedCount.current;
  const incompleteCount = incompletePagination?.totalItems ?? preservedIncompleteCount.current;
  const screenedOutCount = screenedOutPagination?.totalItems ?? preservedScreenedOutCount.current;

  // Calculate total count based on active view
  const totalCount = isCompletedView
    ? completedCount
    : isIncompleteView
      ? incompleteCount
      : screenedOutCount;

  return {
    isLoading,
    completed: shouldFetchCompleted ? completedConversations || [] : [],
    incomplete: shouldFetchIncomplete ? incompleteConversations || [] : [],
    screenedOut: shouldFetchScreenedOut ? screenedOutConversations || [] : [],
    completedCount,
    incompleteCount,
    screenedOutCount,
    totalCount,
  };
};
